/**
 * Application Routes Configuration
 * Following constitution requirements for navigation structure
 *
 * This file defines all application routes, their access levels,
 * and metadata for consistent navigation handling.
 */

/**
 * Route access levels
 */
export const ROUTE_ACCESS = {
  PUBLIC: 'public',           // Accessible without authentication
  AUTHENTICATED: 'authenticated', // Requires valid JWT token
  SUBSCRIPTION: 'subscription',   // Requires active subscription
  ADMIN: 'admin'             // Requires admin role
};

/**
 * User roles (matching backend implementation)
 */
export const USER_ROLES = {
  USER: 'User',
  ADMIN: 'Admin',
  SUPER_ADMIN: 'Super Admin'
};

/**
 * Application routes configuration
 */
export const ROUTES = {
  // Public routes
  HOME: {
    path: '/',
    name: 'home',
    access: ROUTE_ACCESS.PUBLIC,
    component: 'PublicHomepage',
    title: { bg: 'Начало', en: 'Home' },
    description: { bg: 'Главна страница на ArbiWin', en: 'ArbiWin Homepage' },
    showInNavigation: true,
    exact: true
  },

  LOGIN: {
    path: '/login',
    name: 'login',
    access: ROUTE_ACCESS.PUBLIC,
    component: 'LoginPage',
    title: { bg: 'Вход', en: 'Login' },
    description: { bg: 'Вход в системата', en: 'Login to system' },
    showInNavigation: false,
    redirectIfAuthenticated: '/dashboard'
  },

  REGISTER: {
    path: '/register',
    name: 'register',
    access: ROUTE_ACCESS.PUBLIC,
    component: 'RegisterPage',
    title: { bg: 'Регистрация', en: 'Register' },
    description: { bg: 'Създаване на нов акаунт', en: 'Create new account' },
    showInNavigation: false,
    redirectIfAuthenticated: '/dashboard'
  },

  FORGOT_PASSWORD: {
    path: '/forgot-password',
    name: 'forgotPassword',
    access: ROUTE_ACCESS.PUBLIC,
    component: 'ForgotPasswordPage',
    title: { bg: 'Забравена парола', en: 'Forgot Password' },
    description: { bg: 'Възстановяване на парола', en: 'Password recovery' },
    showInNavigation: false
  },

  RESET_PASSWORD: {
    path: '/reset-password',
    name: 'resetPassword',
    access: ROUTE_ACCESS.PUBLIC,
    component: 'ResetPasswordPage',
    title: { bg: 'Нова парола', en: 'New Password' },
    description: { bg: 'Задаване на нова парола', en: 'Set new password' },
    showInNavigation: false
  },

  // Authenticated routes
  DASHBOARD: {
    path: '/dashboard',
    name: 'dashboard',
    access: ROUTE_ACCESS.AUTHENTICATED,
    component: 'Dashboard',
    title: { bg: 'Табло', en: 'Dashboard' },
    description: { bg: 'Потребителско табло', en: 'User dashboard' },
    showInNavigation: true,
    icon: 'dashboard'
  },

  PROFILE: {
    path: '/profile',
    name: 'profile',
    access: ROUTE_ACCESS.AUTHENTICATED,
    component: 'UserProfile',
    title: { bg: 'Профил', en: 'Profile' },
    description: { bg: 'Потребителски профил', en: 'User profile' },
    showInNavigation: true,
    icon: 'user'
  },

  // Subscription-protected routes
  SUREBETS_ALL: {
    path: '/sure-bets/all',
    name: 'surebetsAll',
    access: ROUTE_ACCESS.SUBSCRIPTION,
    component: 'SurebetList',
    title: { bg: 'Всички арбитражи', en: 'All Arbitrages' },
    description: { bg: 'Списък с всички арбитражни възможности', en: 'List of all arbitrage opportunities' },
    showInNavigation: true,
    icon: 'trending-up'
  },

  SUREBETS_FILTERED: {
    path: '/sure-bets/filtered',
    name: 'surebetsFiltered',
    access: ROUTE_ACCESS.SUBSCRIPTION,
    component: 'SurebetFilteredList',
    title: { bg: 'Филтрирани арбитражи', en: 'Filtered Arbitrages' },
    description: { bg: 'Филтрирани арбитражни възможности', en: 'Filtered arbitrage opportunities' },
    showInNavigation: true,
    icon: 'filter'
  },

  CALCULATOR: {
    path: '/calculator',
    name: 'calculator',
    access: ROUTE_ACCESS.SUBSCRIPTION,
    component: 'Calculator',
    title: { bg: 'Калкулатор', en: 'Calculator' },
    description: { bg: 'Калкулатор за арбитражни залози', en: 'Arbitrage calculator' },
    showInNavigation: true,
    icon: 'calculator'
  },

  SUBSCRIPTION_MANAGE: {
    path: '/subscription',
    name: 'subscription',
    access: ROUTE_ACCESS.AUTHENTICATED,
    component: 'SubscriptionManagement',
    title: { bg: 'Абонамент', en: 'Subscription' },
    description: { bg: 'Управление на абонамент', en: 'Subscription management' },
    showInNavigation: true,
    icon: 'credit-card'
  },

  // Admin routes
  ADMIN_DASHBOARD: {
    path: '/admin',
    name: 'adminDashboard',
    access: ROUTE_ACCESS.ADMIN,
    component: 'AdminDashboard',
    title: { bg: 'Администрация', en: 'Administration' },
    description: { bg: 'Административно табло', en: 'Admin dashboard' },
    showInNavigation: false,
    requiredRole: USER_ROLES.ADMIN
  },

  ADMIN_USERS: {
    path: '/admin/users',
    name: 'adminUsers',
    access: ROUTE_ACCESS.ADMIN,
    component: 'AdminUsers',
    title: { bg: 'Потребители', en: 'Users' },
    description: { bg: 'Управление на потребители', en: 'User management' },
    showInNavigation: false,
    requiredRole: USER_ROLES.ADMIN
  },

  ADMIN_SUBSCRIPTIONS: {
    path: '/admin/subscriptions',
    name: 'adminSubscriptions',
    access: ROUTE_ACCESS.ADMIN,
    component: 'AdminSubscriptions',
    title: { bg: 'Абонаменти', en: 'Subscriptions' },
    description: { bg: 'Управление на абонаменти', en: 'Subscription management' },
    showInNavigation: false,
    requiredRole: USER_ROLES.ADMIN
  }
};

/**
 * Route utilities
 */
export const routeUtils = {
  /**
   * Get routes by access level
   * @param {string} accessLevel - Access level to filter by
   * @returns {Array} Filtered routes
   */
  getRoutesByAccess: function(accessLevel) {
    return Object.values(ROUTES).filter(function(route) {
      return route.access === accessLevel;
    });
  },

  /**
   * Get navigation routes (showInNavigation: true)
   * @param {string} userRole - Current user role
   * @returns {Array} Navigation routes
   */
  getNavigationRoutes: function(userRole) {
    return Object.values(ROUTES).filter(function(route) {
      if (!route.showInNavigation) return false;

      // Check role requirements
      if (route.requiredRole && route.requiredRole !== userRole) {
        return false;
      }

      return true;
    });
  },

  /**
   * Get route by name
   * @param {string} routeName - Route name
   * @returns {Object|null} Route object or null
   */
  getRouteByName: function(routeName) {
    return Object.values(ROUTES).find(function(route) {
      return route.name === routeName;
    }) || null;
  },

  /**
   * Get route by path
   * @param {string} path - Route path
   * @returns {Object|null} Route object or null
   */
  getRouteByPath: function(path) {
    return Object.values(ROUTES).find(function(route) {
      return route.path === path;
    }) || null;
  },

  /**
   * Check if user can access route
   * @param {Object} route - Route object
   * @param {Object} user - User object with role and subscription
   * @returns {boolean} True if user can access route
   */
  canUserAccessRoute: function(route, user) {
    // Public routes are always accessible
    if (route.access === ROUTE_ACCESS.PUBLIC) {
      return true;
    }

    // Check authentication
    if (!user || !user.isAuthenticated) {
      return false;
    }

    // Check subscription requirement
    if (route.access === ROUTE_ACCESS.SUBSCRIPTION) {
      if (!user.hasActiveSubscription) {
        return false;
      }
    }

    // Check admin role requirement
    if (route.access === ROUTE_ACCESS.ADMIN) {
      if (user.role !== USER_ROLES.ADMIN && user.role !== USER_ROLES.SUPER_ADMIN) {
        return false;
      }
    }

    // Check specific role requirement
    if (route.requiredRole && route.requiredRole !== user.role) {
      return false;
    }

    return true;
  },

  /**
   * Get redirect path for unauthorized access
   * @param {Object} route - Route object
   * @param {Object} user - User object
   * @returns {string} Redirect path
   */
  getRedirectPath: function(route, user) {
    if (!user || !user.isAuthenticated) {
      return '/login';
    }

    if (route.access === ROUTE_ACCESS.SUBSCRIPTION && !user.hasActiveSubscription) {
      return '/subscription';
    }

    if (route.access === ROUTE_ACCESS.ADMIN && user.role === USER_ROLES.USER) {
      return '/dashboard';
    }

    return '/';
  }
};

/**
 * Route path builders for dynamic routes
 */
export const routeBuilders = {
  /**
   * Build surebet detail route
   * @param {number} surebetId - Surebet ID
   * @returns {string} Route path
   */
  surebetDetail: function(surebetId) {
    return '/sure-bets/' + surebetId;
  },

  /**
   * Build calculator route with pre-filled data
   * @param {number} surebetId - Surebet ID
   * @param {string} marginType - Margin type
   * @returns {string} Route path with query parameters
   */
  calculatorWithData: function(surebetId, marginType) {
    return '/calculator?surebet=' + surebetId + '&margin=' + marginType;
  },

  /**
   * Build admin user detail route
   * @param {number} userId - User ID
   * @returns {string} Route path
   */
  adminUserDetail: function(userId) {
    return '/admin/users/' + userId;
  }
};

/**
 * Default route redirects based on user state
 */
export const DEFAULT_REDIRECTS = {
  UNAUTHENTICATED: '/',
  AUTHENTICATED_NO_SUBSCRIPTION: '/subscription',
  AUTHENTICATED_WITH_SUBSCRIPTION: '/sure-bets/all',
  ADMIN: '/admin',
  AFTER_LOGIN: '/dashboard',
  AFTER_REGISTRATION: '/dashboard',
  AFTER_SUBSCRIPTION_ACTIVATION: '/sure-bets/all'
};

export default ROUTES;
